import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Users, Search, Download, ChevronDown, ChevronRight, IndianRupee, 
  Calendar, Target, Mail, UserCheck, Clock, CheckCircle, AlertCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const STATUS_STYLES = {
  pending_payment: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  completed: "bg-blue-100 text-blue-700"
};

const STATUS_LABELS = {
  pending_payment: "Pending",
  paid: "Paid",
  completed: "Completed"
};

export default function AdminParticipants() {
  const [participants, setParticipants] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTraining, setSelectedTraining] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedRows, setSelectedRows] = useState(new Set());

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/participants`, authHeader()),
      axios.get(`${API}/admin/trainings`, authHeader()),
    ]).then(([pRes, tRes]) => {
      const participantsData = pRes.data.data || pRes.data;
      setParticipants(participantsData);
      setTrainings(tRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Calculate training-wise stats
  const trainingStats = useMemo(() => {
    const stats = {};
    
    // Initialize stats for each training
    trainings.forEach(t => {
      stats[t.id] = {
        id: t.id,
        title: t.title,
        date: t.date,
        price: t.price,
        seats: t.seats,
        seats_filled: t.seats_filled || 0,
        total: 0,
        paid: 0,
        pending: 0,
        completed: 0,
        revenue: 0
      };
    });
    
    // Count participants per training
    participants.forEach(p => {
      if (stats[p.training_id]) {
        stats[p.training_id].total++;
        if (p.registration_status === 'paid') {
          stats[p.training_id].paid++;
          stats[p.training_id].revenue += trainings.find(t => t.id === p.training_id)?.price || 0;
        } else if (p.registration_status === 'completed') {
          stats[p.training_id].completed++;
          stats[p.training_id].revenue += trainings.find(t => t.id === p.training_id)?.price || 0;
        } else {
          stats[p.training_id].pending++;
        }
      }
    });
    
    return stats;
  }, [participants, trainings]);

  // Overall summary stats
  const summaryStats = useMemo(() => {
    const paid = participants.filter(p => p.registration_status === 'paid' || p.registration_status === 'completed').length;
    const pending = participants.filter(p => p.registration_status === 'pending_payment').length;
    const totalRevenue = Object.values(trainingStats).reduce((sum, t) => sum + t.revenue, 0);
    return { total: participants.length, paid, pending, revenue: totalRevenue };
  }, [participants, trainingStats]);

  // Filter participants
  const filtered = useMemo(() => {
    return participants.filter(p => {
      const matchSearch = search === "" ||
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.email?.toLowerCase().includes(search.toLowerCase()) ||
        p.phone?.includes(search);
      const matchTraining = selectedTraining === "all" || p.training_id === selectedTraining;
      const matchStatus = filterStatus === "all" || p.registration_status === filterStatus;
      return matchSearch && matchTraining && matchStatus;
    });
  }, [participants, search, selectedTraining, filterStatus]);

  // Get selected training details
  const selectedTrainingDetails = selectedTraining !== "all" ? trainingStats[selectedTraining] : null;

  // Toggle row selection
  const toggleRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Toggle all rows
  const toggleAll = () => {
    if (selectedRows.size === filtered.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filtered.map(p => p.id)));
    }
  };

  // Export CSV
  const exportCSV = () => {
    const dataToExport = selectedRows.size > 0 
      ? filtered.filter(p => selectedRows.has(p.id))
      : filtered;
    
    const headers = ["Name", "Email", "Phone", "Training", "Status", "Registered At"];
    const rows = dataToExport.map(p => [
      p.name, p.email, p.phone, p.training_title, p.registration_status,
      new Date(p.created_at).toLocaleDateString('en-IN')
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c || ''}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; 
    a.download = selectedTraining === "all" ? "all_participants.csv" : `participants_${selectedTrainingDetails?.title?.replace(/\s+/g, '_')}.csv`; 
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-gray-900">Participants</h1>
          <p className="text-gray-500 text-sm font-dm-sans mt-1">Manage registrations by training</p>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users size={18} className="text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-outfit font-bold text-gray-900">{summaryStats.total}</div>
              <div className="text-xs text-gray-500 font-dm-sans">Total Registrations</div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <UserCheck size={18} className="text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-outfit font-bold text-emerald-600">{summaryStats.paid}</div>
              <div className="text-xs text-gray-500 font-dm-sans">Paid ({summaryStats.total > 0 ? Math.round(summaryStats.paid / summaryStats.total * 100) : 0}%)</div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock size={18} className="text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-outfit font-bold text-amber-600">{summaryStats.pending}</div>
              <div className="text-xs text-gray-500 font-dm-sans">Pending Payment</div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-gray-200 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <IndianRupee size={18} className="text-violet-600" />
            </div>
            <div>
              <div className="text-2xl font-outfit font-bold text-violet-600">₹{summaryStats.revenue.toLocaleString('en-IN')}</div>
              <div className="text-xs text-gray-500 font-dm-sans">Total Revenue</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Training Tabs */}
      <div className="bg-white border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex flex-wrap items-stretch">
            {/* All Tab */}
            <button
              data-testid="tab-all"
              onClick={() => { setSelectedTraining("all"); setSelectedRows(new Set()); }}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium font-dm-sans whitespace-nowrap border-b-2 transition-colors ${
                selectedTraining === "all"
                  ? "border-black text-black bg-gray-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Users size={14} />
              All
              <span className={`text-xs px-1.5 py-0.5 rounded ${selectedTraining === "all" ? "bg-black text-white" : "bg-gray-200 text-gray-600"}`}>
                {participants.length}
              </span>
            </button>

            {/* Training Tabs (all shown, wraps onto multiple lines) */}
            {trainings.map((training) => {
              const stats = trainingStats[training.id] || {};
              return (
                <button
                  key={training.id}
                  data-testid={`tab-${training.id}`}
                  onClick={() => { setSelectedTraining(training.id); setSelectedRows(new Set()); }}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium font-dm-sans whitespace-nowrap border-b-2 transition-colors ${
                    selectedTraining === training.id
                      ? "border-black text-black bg-gray-50"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                  title={training.title}
                >
                  <span className="max-w-56 truncate">{training.title}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${selectedTraining === training.id ? "bg-black text-white" : "bg-gray-200 text-gray-600"}`}>
                    {stats.total || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Training Info Bar */}
        {selectedTrainingDetails && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-5 py-3"
          >
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="font-outfit font-bold text-gray-900">{selectedTrainingDetails.title}</div>
              <div className="flex items-center gap-1 text-gray-500 font-dm-sans">
                <Calendar size={13} />
                {selectedTrainingDetails.date}
              </div>
              <div className="flex items-center gap-1 text-emerald-600 font-bold font-dm-sans">
                <UserCheck size={13} />
                {selectedTrainingDetails.paid + selectedTrainingDetails.completed} Paid
              </div>
              <div className="flex items-center gap-1 text-amber-600 font-dm-sans">
                <Clock size={13} />
                {selectedTrainingDetails.pending} Pending
              </div>
              <div className="flex items-center gap-1 text-violet-600 font-bold font-dm-sans">
                <IndianRupee size={13} />
                ₹{selectedTrainingDetails.revenue.toLocaleString('en-IN')}
              </div>
              <div className="flex items-center gap-1 text-gray-500 font-dm-sans">
                <Target size={13} />
                {selectedTrainingDetails.seats_filled}/{selectedTrainingDetails.seats} seats
              </div>
            </div>
          </motion.div>
        )}

        {/* Search and Filters */}
        <div className="p-4 border-b border-gray-100 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="search-participants-input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, phone..."
              className="w-full border border-gray-200 focus:border-gray-400 pl-9 pr-3 py-2 text-sm font-dm-sans outline-none rounded-sm"
            />
          </div>
          
          <div className="relative">
            <select
              data-testid="filter-status-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="border border-gray-200 focus:border-gray-400 pl-3 pr-8 py-2 text-sm font-dm-sans outline-none appearance-none bg-white rounded-sm"
            >
              <option value="all">All Status</option>
              <option value="pending_payment">Pending Payment</option>
              <option value="paid">Paid</option>
              <option value="completed">Completed</option>
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          <button
            data-testid="export-csv-btn"
            onClick={exportCSV}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 font-dm-sans text-sm px-4 py-2 hover:bg-gray-50 transition-colors rounded-sm"
          >
            <Download size={14} />
            Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ""}
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Users size={40} className="text-gray-300 mx-auto mb-3" />
            <h3 className="font-outfit font-bold text-gray-700 text-lg mb-1">No participants found</h3>
            <p className="text-gray-400 text-sm font-dm-sans">
              {participants.length === 0 
                ? "Participants will appear here once someone registers" 
                : "Try adjusting your filters"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-dm-sans">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedRows.size === filtered.length && filtered.length > 0}
                        onChange={toggleAll}
                        className="w-4 h-4 accent-black"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Participant</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Contact</th>
                    {selectedTraining === "all" && (
                      <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Training</th>
                    )}
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Registered</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i * 0.02, 0.5) }}
                      data-testid={`participant-row-${p.id}`}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedRows.has(p.id) ? "bg-blue-50" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(p.id)}
                          onChange={() => toggleRow(p.id)}
                          className="w-4 h-4 accent-black"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                            {p.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <div className="font-medium text-gray-900">{p.name}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-600">{p.email}</div>
                        <div className="text-gray-400 text-xs">{p.phone}</div>
                      </td>
                      {selectedTraining === "all" && (
                        <td className="px-4 py-3">
                          <div className="text-gray-700 max-w-40 truncate">{p.training_title}</div>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${STATUS_STYLES[p.registration_status] || STATUS_STYLES.pending_payment}`}>
                          {STATUS_LABELS[p.registration_status] || p.registration_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(p.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-gray-400 font-mono">{p.payment_order_id || '-'}</code>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500 font-dm-sans">
                {selectedRows.size > 0 
                  ? `${selectedRows.size} selected`
                  : `Showing ${filtered.length} of ${participants.length} participants`
                }
              </p>
              {selectedRows.size > 0 && (
                <button
                  onClick={() => setSelectedRows(new Set())}
                  className="text-xs text-blue-600 hover:text-blue-800 font-dm-sans"
                >
                  Clear selection
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
